// client/src/components/admin/result/RejectedResultsList.js
import React, { useState, useEffect, useCallback } from 'react';
import ProjectDetailModal from '../../marketer/project/modals/ProjectDetailModal';
import ResultViewModal from '../../marketer/result/modals/ResultViewModal';

const API_URL = process.env.REACT_APP_API_URL;

export default function RejectedResultsList() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailData, setDetailData] = useState(null);
  
  // 모달 상태
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // 결과물 선택 및 상세 조회
  const handleSelectResult = useCallback(async (result) => {
    setSelectedResult(result);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/results/${result.applicationId}`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setDetailData(data);
      } else {
        alert('상세 정보 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('상세 정보 조회 오류:', error);
      alert('상세 정보 조회 중 오류가 발생했습니다.');
    }
  }, []);

  // 거절된 결과물 목록 조회
  const fetchRejectedResults = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/results/rejected-list?limit=100`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setResults(data.results || []);
        // 첫 번째 항목 자동 선택
        if (data.results && data.results.length > 0) {
          handleSelectResult(data.results[0]);
        }
      } else {
        alert('목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('거절 건 목록 조회 오류:', error);
      alert('목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [handleSelectResult]);

  useEffect(() => {
    fetchRejectedResults();
  }, [fetchRejectedResults]);

  // 프로젝트 상세보기
  const handleShowProjectDetail = () => {
    if (detailData?.project) {
      setShowProjectModal(true);
    }
  };

  // 결과물 보기
  const handleShowResult = () => {
    if (detailData?.application) {
      setShowResultModal(true);
    }
  };

  // 결과물 승인 처리
  const handleApprove = async () => {
    if (!window.confirm('이 결과물을 최종 승인하시겠습니까?\n승인 시 인플루언서에게 즉시 포인트가 지급됩니다.')) {
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/results/${selectedResult.applicationId}/admin-approve`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        alert('승인이 완료되었습니다.');
        setShowApproveModal(false);
        fetchRejectedResults();
        setSelectedResult(null);
        setDetailData(null);
      } else {
        alert(data.message || '승인에 실패했습니다.');
      }
    } catch (error) {
      console.error('승인 처리 오류:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 결과물 거절 확정 처리
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }

    if (!window.confirm('이 결과물을 최종 거절하시겠습니까?\n거절 시 계약이 취소됩니다.')) {
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/results/${selectedResult.applicationId}/admin-reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rejectionReason })
      });
      const data = await res.json();
      
      if (data.ok) {
        alert('거절 확정이 완료되었습니다.');
        setShowRejectModal(false);
        setRejectionReason('');
        fetchRejectedResults();
        setSelectedResult(null);
        setDetailData(null);
      } else {
        alert(data.message || '거절 확정에 실패했습니다.');
      }
    } catch (error) {
      console.error('거절 확정 처리 오류:', error);
      alert('거절 확정 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="rejected-results-list">
      <h2 className="rejected-results-list__title">결과물 거절 건 처리</h2>

      {/* 로딩 */}
      {loading && <div className="rejected-results-list__loading">로딩 중...</div>}

      {/* 목록 없음 */}
      {!loading && results.length === 0 && (
        <div className="rejected-results-list__empty">
          처리할 거절 건이 없습니다.
        </div>
      )}

      {/* 메인 컨텐츠 */}
      {!loading && results.length > 0 && (
        <div className="rejected-results-list__content">
          {/* 왼쪽: 목록 */}
          <div className="rejected-results-list__left">
            <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 700 }}>
              거절 건 목록 ({results.length})
            </h3>
            
            <div className="rejected-results-list__items">
              {results.map((result) => (
                <div
                  key={result.applicationId}
                  onClick={() => handleSelectResult(result)}
                  className={`rejected-results-list__item ${
                    selectedResult?.applicationId === result.applicationId ? 'rejected-results-list__item--selected' : ''
                  }`}
                >
                  <div className="rejected-results-list__item-project">
                    {result.projectNumber}
                  </div>
                  <div className="rejected-results-list__item-influencer">
                    광고주: {result.marketerName}
                  </div>
                  <div className="rejected-results-list__item-influencer">
                    인플루언서: {result.influencerName} ({result.influencerUserId})
                  </div>
                  <div className="rejected-results-list__item-date">
                    거절: {formatDate(result.rejectedAt)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 오른쪽: 상세 정보 (이 부분은 RejectedResultsSummary로 분리됨) */}
          <div style={{
            border: '2px solid #e5e7eb',
            borderRadius: 12,
            padding: 20,
            background: '#fff',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            {!selectedResult && (
              <div className="rejected-results-list__empty">
                왼쪽 목록에서 거절 건을 선택하세요.
              </div>
            )}

            {selectedResult && detailData && (
              <>
                <h3 style={{ marginBottom: 20, fontSize: 18, fontWeight: 700 }}>
                  거절 건 상세 정보
                </h3>

                {/* 기본 정보 */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                    프로젝트 정보
                  </div>
                  <div style={{ 
                    padding: 16, 
                    background: '#f9fafb', 
                    borderRadius: 8,
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>프로젝트 번호: </span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedResult.projectNumber}</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>광고주: </span>
                      <span style={{ fontSize: 13 }}>{detailData.application.marketerName}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>인플루언서: </span>
                      <span style={{ fontSize: 13 }}>
                        {detailData.application.influencer.userName} ({detailData.application.influencer.userId})
                      </span>
                    </div>
                  </div>
                </div>

                {/* 거절 정보 */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                    거절 정보
                  </div>
                  <div style={{ 
                    padding: 16, 
                    background: '#fef2f2', 
                    borderRadius: 8,
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>거절 일시: </span>
                      <span style={{ fontSize: 13 }}>{formatDate(detailData.application.resultRejectedAt)}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>거절 사유: </span>
                      <div style={{ 
                        fontSize: 13, 
                        marginTop: 4, 
                        padding: 8, 
                        background: '#fff',
                        borderRadius: 4
                      }}>
                        {detailData.application.resultRejectionReason || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 액션 버튼 */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  marginTop: 24
                }}>
                  <button
                    onClick={handleShowProjectDetail}
                    style={{
                      padding: '12px 20px',
                      background: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    📋 프로젝트 상세보기
                  </button>

                  <button
                    onClick={handleShowResult}
                    style={{
                      padding: '12px 20px',
                      background: '#8b5cf6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    📸 결과물 보기
                  </button>

                  <button
                    onClick={() => setShowApproveModal(true)}
                    disabled={processing}
                    style={{
                      padding: '12px 20px',
                      background: processing ? '#9ca3af' : '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processing ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    ✅ 결과물 승인 처리
                  </button>

                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                    style={{
                      padding: '12px 20px',
                      background: processing ? '#9ca3af' : '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processing ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    ❌ 결과물 거절 확정 처리
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 프로젝트 상세보기 모달 */}
      {showProjectModal && detailData?.project && (
        <ProjectDetailModal
          open={showProjectModal}
          onClose={() => setShowProjectModal(false)}
          project={detailData.project}
        />
      )}

      {/* 결과물 보기 모달 */}
      {showResultModal && detailData?.application && (
        <ResultViewModal
          open={showResultModal}
          onClose={() => setShowResultModal(false)}
          result={{
            ...detailData.application,
            // 인플루언서 정보 추가
            influencerInfo: {
              userName: detailData.application.influencer.userName,
              userId: detailData.application.influencer.userId,
              email: detailData.application.influencer.email,
              phoneNumber: detailData.application.influencer.phoneNumber
            }
          }}
        />
      )}

      {/* 승인 확인 모달 */}
      {showApproveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 400,
            width: '90%'
          }}>
            <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 700 }}>
              결과물 승인 처리
            </h3>
            <p style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
              이 결과물을 최종 승인하시겠습니까?<br/>
              승인 시 인플루언서에게 즉시 포인트가 지급되며,<br/>
              광고주의 결과물 승인/거절 화면에서<br/>
              <strong>"셀프애드팀 결과물 승인 처리"</strong>로 표시됩니다.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#e5e7eb',
                  border: 'none',
                  borderRadius: 6,
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                취소
              </button>
              <button
                onClick={handleApprove}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: processing ? '#9ca3af' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                {processing ? '처리 중...' : '승인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 거절 확정 모달 */}
      {showRejectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            width: '90%'
          }}>
            <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 700 }}>
              결과물 거절 확정 처리
            </h3>
            <p style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
              이 결과물을 최종 거절하시겠습니까?<br/>
              거절 시 계약이 취소되며 포인트가 지급되지 않습니다.<br/>
              인플루언서의 결과물 제출 화면에서<br/>
              <strong>"결과물 미제출로 인한 계약 위반 건"</strong>으로 표시됩니다.
            </p>
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ 
                display: 'block', 
                marginBottom: 8, 
                fontSize: 13, 
                fontWeight: 600 
              }}>
                거절 사유 (필수)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요..."
                disabled={processing}
                style={{
                  width: '100%',
                  height: 100,
                  padding: 12,
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#e5e7eb',
                  border: 'none',
                  borderRadius: 6,
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                취소
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: (processing || !rejectionReason.trim()) ? '#9ca3af' : '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: (processing || !rejectionReason.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {processing ? '처리 중...' : '거절 확정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

